import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import SoundHuntRoundView from './SoundHuntRoundView';
import LetterHuntRoundView from './LetterHuntRoundView';
import SoundChoiceRoundView from './SoundChoiceRoundView';
import MemoryTrainRoundView from './MemoryTrainRoundView';
import practiceProgress from './practiceProgress';
import sequentialProgress from './sequentialProgress';
import MixedFinalRoundView from './MixedFinalRoundView';
import WordAdventureRoundView from './WordAdventureRoundView';
import BalloonPopRoundView from './BalloonPopRoundView';
import HandwritingGame from '../duy-ve-yaz/HandwritingGame';
import handwritingProgress from '../duy-ve-yaz/handwritingProgress';
import { completeTurkishL1Tour } from '../miniTasksApi';
import { GardenBackdrop } from '../components/RoundView';

// A tour keeps its questions; one submission follows all of its games.
export default function TurkishL1TourView({ round, boardSessionId, onComplete, onExit, ...props }) {
  const [index, setIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const sharedAudioRef = useRef(null);
  const results = useRef({});
  const submission = useRef(null);
  const busy = useRef(false);
  const alive = useRef(true);
  const completeRef = useRef(onComplete);
  completeRef.current = onComplete;
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const localProgress = useCallback((gameId, events) => {
    const game = round.games.find((candidate) => candidate.game_id === gameId);
    if (game.assessment_mode === 'handwriting_dictation') {
      const studentDictation = !round.preview && game.student_dictation === true
        && game.input_mode === 'letter_slots' && game.ocr_enabled === false;
      if (!studentDictation && !(round.preview && game.preview_only)) throw new Error('Yazma etkinliği açılamadı.');
      const { completed, outcomes, summary } = handwritingProgress(game, events);
      if (completed) results.current[gameId] = { game_id: gameId, completed,
        ...(studentDictation ? { dictation_events: events.map(event => ({ ...event })) } : { outcomes }), summary };
      return { round_completed: completed, summary };
    }
    const sourceLevel = game.source_level || round.level || (round.level_id === 'tr-g1-l02' ? 2 : 1);
    const unmeasuredHelp = sourceLevel === 2 || sourceLevel >= 4;
    if (['balloon_pop', 'syllable_choice', 'mixed_final', 'word_construction', 'syllable_bridge', 'word_repair', 'silent_word_chest'].includes(game.assessment_mode)
      || (game.assessment_mode === 'syllable_train' && sourceLevel >= 2)) {
      const { answers, summary, completed } = sequentialProgress(game, events);
      if (completed) results.current[gameId] = { game_id: gameId, completed: true, answer_events: answers,
        ...(unmeasuredHelp ? {} : { help_count: summary.help_count }), summary };
      return { round_completed: completed, summary, next_round: completed ? round.games[round.games.indexOf(game) + 1] : null };
    }
    if (['letter_memory', 'syllable_train'].includes(game.assessment_mode)) {
      const { answers, summary, completed } = practiceProgress(game, events);
      if (completed) results.current[gameId] = { game_id: gameId, completed: true, answer_events: answers,
        ...(unmeasuredHelp ? {} : { help_count: summary.help_count }), summary };
      return { round_completed: completed, summary, next_round: completed ? round.games[round.games.indexOf(game) + 1] : null };
    }
    const choiceGame = game.assessment_mode === 'sound_choice';
    const targets = new Set(game.items.filter((item) => item.is_target ?? item.contains_target_sound).map((item) => item.id));
    const answers = events.filter((event) => event.kind === 'select').map((event) => choiceGame
      ? { item_id: event.item_id, selected_id: event.selected_id } : { selected_id: event.item_id });
    const found = new Set();
    let first = 0, wrong = 0, segment = 0;
    let cursor = 0;
    answers.forEach(({ selected_id: id }) => {
      segment += 1;
      if (choiceGame ? id === game.items[cursor]?.target_sound : targets.has(id)) {
        first += Number(segment === 1); found.add(choiceGame ? game.items[cursor++].id : id); segment = 0;
      }
      else wrong += 1;
    });
    const screenAssessment = game.assessment_mode === 'screen_hunt';
    const targetTotal = choiceGame ? game.items.length : targets.size;
    const total = screenAssessment ? 1 : targetTotal;
    const completed = found.size === targetTotal;
    const help = Number(events.some((event) => event.kind === 'help_prompt'));
    const summary = { game_id: gameId, completed, first_try_correct: screenAssessment ? Number(completed && wrong === 0) : first, total_assessments: total,
      wrong_selection_count: wrong, help_count: help };
    if (unmeasuredHelp) delete summary.help_count;
    if (completed) results.current[gameId] = { game_id: gameId, completed: true, answer_events: answers,
      ...(unmeasuredHelp ? {} : { help_count: help }), summary };
    return { round_completed: completed, summary, next_round: completed ? round.games[round.games.indexOf(game) + 1] : null };
  }, [round]);
  const next = useCallback(() => {
    // A short laptop may scroll down to reach the last train letter. The next
    // game is a new screen, so don't leave its navigation/audio controls above
    // the viewport. Keep JSDOM safe by calling scrollTo only when scrolled.
    if (typeof window !== 'undefined' && window.scrollY > 0) window.scrollTo(0, 0);
    setIndex((value) => value + 1);
  }, []);
  const finish = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    setSaving(true); setError('');
    try {
      if (!submission.current) submission.current = {
        level_id: round.level_id, content_version: round.content_version, tour: round.tour, snapshot_id: round.snapshot_id,
        games: round.games.map(({ game_id }) => {
          const { summary, ...result } = results.current[game_id];
          return result;
        }),
      };
      const isBalloonFinale = round.games.length === 1 && round.games[0]?.assessment_mode === 'balloon_pop';
      const isHandwritingPreview = round.games.length === 1 && round.games[0]?.assessment_mode === 'handwriting_dictation';
      const response = round.preview ? { summary: {
        preview: true, task_type: 'turkish_l1_tour', tour: round.tour, rounds_remaining: 0,
        next_prompt: isHandwritingPreview ? (results.current.G12?.summary?.practice_ended_early ? 'Bugün güzel çalıştın!' : 'Duy ve Yaz tamamlandı!') : isBalloonFinale ? 'Yaşasın! Balonları patlattın! 🎈' : round.is_endless ? 'Türkçe karma tekrarını tamamladın!' : round.is_review
          ? round.tour === 5 ? 'L1 ve L2 karma tekrarını tamamladın!' : 'Bugünkü Türkçe karma tekrarını tamamladın!'
          : round.tour === (round.tour_count || 5) ? round.level === 5 ? 'İlk sözcük maceranı tamamladın!' : `L${round.level || 1} ses oyunlarını tamamladın!` : 'Bugünkü Türkçe oyunlarını tamamladın!',
        ...(isBalloonFinale ? { is_balloon_finale: true } : {}),
        ...(isHandwritingPreview ? { is_handwriting_preview: true } : {}),
        ...(round.is_review ? { is_review: true, source_levels: round.source_levels } : {}),
        ...(round.is_endless ? { is_endless: true } : {}),
        level_completed: !isHandwritingPreview && !round.is_endless && round.tour === (round.tour_count || 5), games: round.games.map(({ game_id }) => results.current[game_id].summary),
      } } : await completeTurkishL1Tour(round.round_id, boardSessionId, submission.current);
      if (alive.current) completeRef.current(response.summary);
    } catch (err) { if (alive.current) setError(err.message || 'Tur kaydedilemedi. Yeniden deneyelim.'); }
    finally { busy.current = false; }
  }, [round, boardSessionId]);
  const game = useMemo(() => {
    const saved = round.games[index];
    // Shared presentation media also serves already-started frozen G12 rounds.
    // Targets, snapshot ID and measurement continue to use the saved game.
    const audio = saved.assessment_mode === 'handwriting_dictation' ? round.dictation_audio : null;
    return { ...saved, ...audio, audio: { ...saved.audio, ...audio?.audio },
      round_id: round.round_id, student: round.student,
      preview: round.preview, local_tour: true, v22: true, continuing: index > 0,
      next_round: round.games[index + 1] || null };
  }, [round, index]);
  if (saving) return <Box className="sound-hunt" sx={{ display: 'grid', placeItems: 'center' }}>
    <GardenBackdrop />
    <Box sx={{ position: 'relative', bgcolor: '#fff', p: 4, borderRadius: 5, textAlign: 'center', maxWidth: 600 }}>
      <Typography variant="h4" sx={{ fontWeight: 900 }}>{round.games.length === 1 ? 'Oyun tamam!' : 'İki oyun da tamam!'}</Typography>
      {error ? <><Alert severity="error" sx={{ my: 2 }}>{error}</Alert><Button size="large" variant="contained" onClick={finish}>Tekrar Dene</Button></>
        : <Typography role="status" sx={{ my: 2 }}>Tur kaydediliyor…</Typography>}
      <Button onClick={onExit} sx={{ display: 'block', mx: 'auto', mt: 2 }}>Ana sayfa</Button>
    </Box>
  </Box>;
  const View = game.assessment_mode === 'handwriting_dictation' ? HandwritingGame
    : game.assessment_mode === 'balloon_pop' ? BalloonPopRoundView
    : ['syllable_bridge', 'word_repair', 'silent_word_chest'].includes(game.assessment_mode) ? WordAdventureRoundView
    : game.assessment_mode === 'mixed_final' ? MixedFinalRoundView
    : ['letter_memory', 'syllable_train', 'word_construction'].includes(game.assessment_mode) ? MemoryTrainRoundView
    : ['sound_choice', 'syllable_choice'].includes(game.assessment_mode) ? SoundChoiceRoundView
    : game.task_type === 'letter_hunt' ? LetterHuntRoundView : SoundHuntRoundView;
  return <View key={game.game_id} {...props} round={game} boardSessionId={boardSessionId}
    classTitle={props.classTitle || round.class_title} soundEnabled sharedAudioRef={sharedAudioRef}
    localProgress={localProgress} onNextRound={next} onComplete={finish} onExit={onExit}
    initialInputMode={game.assessment_mode === 'handwriting_dictation' ? (props.initialInputMode || 'keys') : undefined} />;
}
